import React, { useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import {
  ArrowBackRounded,
  InfoOutlined,
  TrendingUpRounded,
  UndoRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  Deal,
  DealEventType,
  RegistrationStatus,
} from "../../../Models/ambassador";
import {
  useAddMyDealMessage,
  useDeleteMyDealDocument,
  useMyDeal,
  useUploadMyDealDocuments,
  useWithdrawDeal,
} from "../../../Query/useAmbassador";
import AmbassadorConfirmDialog from "../Common/AmbassadorConfirmDialog";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  RegistrationStatusChip,
  SalesStatusChip,
} from "../Common/AmbassadorUi";
import DealConversation from "../Common/DealConversation";
import DealDocuments from "../Common/DealDocuments";
import DealTimeline, { TimelineEntry } from "../Common/DealTimeline";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { formatDate, formatDateTime } from "../Common/format";
import { useAmbassadorLabels } from "../Common/useAmbassadorLabels";
import DealDataSummary from "./DealDataSummary";
import { toFormData } from "./DealForm/dealFormModel";

const REPLY_STATUSES = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.UNDER_REVIEW,
  RegistrationStatus.QUESTIONS_PENDING,
  RegistrationStatus.CONFIRMED,
];
const WITHDRAWABLE_STATUSES = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.UNDER_REVIEW,
  RegistrationStatus.QUESTIONS_PENDING,
];

type TabKey = "overview" | "history" | "questions" | "documents";

/** History entries as the ambassador sees them. */
const useHistoryEntries = (deal: Deal): TimelineEntry[] => {
  const labels = useAmbassadorLabels();
  const { t } = useTranslation("ambassador");
  return deal.history.map((entry) => ({
    id: entry.id,
    created_at: entry.created_at,
    note: entry.note,
    label:
      entry.event_type === DealEventType.SALES_STATUS_CHANGED &&
      entry.sales_status != null
        ? t("history.salesUpdate", {
            status: labels.salesStatus(entry.sales_status),
          })
        : labels.event(entry.event_type),
  }));
};

const DealDetailsContent: React.FC<{ deal: Deal; submitted: boolean }> = ({
  deal,
  submitted,
}) => {
  const { t, i18n } = useTranslation("ambassador");
  const navigate = useNavigate();
  const labels = useAmbassadorLabels();
  const [tab, setTab] = useState<TabKey>(
    deal.registration_status === RegistrationStatus.QUESTIONS_PENDING
      ? "questions"
      : "overview",
  );
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const addMessage = useAddMyDealMessage();
  const uploadDocuments = useUploadMyDealDocuments();
  const deleteDocument = useDeleteMyDealDocument();
  const withdraw = useWithdrawDeal();
  const history = useHistoryEntries(deal);

  const questionsPending =
    deal.registration_status === RegistrationStatus.QUESTIONS_PENDING;
  const openQuestion = [...deal.messages]
    .reverse()
    .find((message) => message.is_question);
  const lastComoMessage = [...deal.messages]
    .reverse()
    .find((message) => !message.from_ambassador);
  const canReply = REPLY_STATUSES.includes(deal.registration_status);

  const overview = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          lg: "minmax(0, 1fr) 380px",
        },
        gap: 3,
        alignItems: "start",
      }}
    >
      <Panel title={t("details.dealInformation")}>
        <DealDataSummary data={toFormData(deal)} />
        <Box
          sx={{
            mt: 3,
            p: 1.5,
            display: "flex",
            gap: 1,
            borderRadius: "10px",
            backgroundColor: color.gray50,
            border: `1px solid ${color.gray200}`,
          }}
        >
          <InfoOutlined
            sx={{ fontSize: 18, color: color.gray500, mt: "1px" }}
          />
          <Typography sx={{ fontSize: 13, color: color.gray600 }}>
            {t("details.noCommissionNote")}
          </Typography>
        </Box>
      </Panel>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Panel title={t("details.statusHistory")}>
          <DealTimeline entries={history} emptyText={t("details.noHistory")} />
        </Panel>

        {deal.registration_status === RegistrationStatus.CONFIRMED && (
          <Panel title={t("details.salesProgress")}>
            {deal.sales_status !== null ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: color.blue50,
                  border: `1px solid ${color.blue200}`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: color.blue700,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <TrendingUpRounded sx={{ fontSize: 18 }} />
                  {t("details.currentSales", {
                    status: labels.salesStatus(deal.sales_status),
                  })}
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: color.gray700, mt: 0.5 }}
                >
                  {t(`details.salesDescriptions.${deal.sales_status}`)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2">{t("details.noSalesYet")}</Typography>
            )}
          </Panel>
        )}

        {lastComoMessage && (
          <Panel title={t("details.messageFromComo")}>
            <Typography
              sx={{
                fontSize: 14,
                color: color.gray900,
                whiteSpace: "pre-wrap",
              }}
            >
              „{lastComoMessage.message}“
            </Typography>
            <Typography sx={{ fontSize: 12, color: color.gray500, mt: 1 }}>
              {formatDate(lastComoMessage.created_at, i18n.language)}
              {lastComoMessage.author_name &&
                ` · ${lastComoMessage.author_name}`}
            </Typography>
          </Panel>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <PageHeader
        title={deal.title}
        subtitle={t("details.subtitle", {
          number: deal.deal_number,
          date: formatDate(deal.submitted_at, i18n.language),
          company: deal.company_name,
        })}
        actions={
          <>
            {WITHDRAWABLE_STATUSES.includes(deal.registration_status) && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<UndoRounded />}
                onClick={() => setWithdrawOpen(true)}
              >
                {t("details.withdraw")}
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate(ambassadorPaths.myDeals)}
            >
              {t("details.backToMyDeals")}
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mt: 1.5,
          }}
        >
          <RegistrationStatusChip status={deal.registration_status} />
          {deal.sales_status !== null && (
            <SalesStatusChip status={deal.sales_status} />
          )}
          <Typography sx={{ fontSize: 12, color: color.gray400 }}>
            {t("details.lastUpdate", {
              date: formatDateTime(deal.last_update_at, i18n.language),
            })}
          </Typography>
        </Box>
      </PageHeader>

      {submitted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t("details.submittedConfirmation", { number: deal.deal_number })}
        </Alert>
      )}
      {questionsPending && openQuestion && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            tab !== "questions" && (
              <Button
                color="inherit"
                size="small"
                onClick={() => setTab("questions")}
              >
                {t("details.answer")}
              </Button>
            )
          }
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {t("details.openQuestion")}
          </Typography>
          <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
            {openQuestion.message}
          </Typography>
        </Alert>
      )}
      {deal.registration_status === RegistrationStatus.REJECTED &&
        deal.rejection_reason && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t("details.rejectedReason", { reason: deal.rejection_reason })}
          </Alert>
        )}

      <Tabs
        value={tab}
        onChange={(_, value: TabKey) => setTab(value)}
        sx={{ mb: 3, borderBottom: `1px solid ${color.gray200}` }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="overview" label={t("details.tabs.overview")} />
        <Tab value="history" label={t("details.tabs.history")} />
        <Tab
          value="questions"
          label={t("details.tabs.questions", { count: deal.messages.length })}
        />
        <Tab
          value="documents"
          label={t("details.tabs.documents", { count: deal.documents.length })}
        />
      </Tabs>

      {tab === "overview" && overview}
      {tab === "history" && (
        <Panel title={t("details.statusHistory")}>
          <DealTimeline entries={history} emptyText={t("details.noHistory")} />
        </Panel>
      )}
      {tab === "documents" && (
        <Panel
          title={t("documents.title")}
          subtitle={t("documents.ambassadorHint")}
        >
          <DealDocuments
            side="ambassador"
            dealId={deal.id}
            documents={deal.documents}
            onUpload={(files) =>
              uploadDocuments.mutateAsync({ dealId: deal.id, files })
            }
            onDelete={(documentId) =>
              deleteDocument.mutateAsync({ dealId: deal.id, documentId })
            }
            // Each side removes the documents it uploaded.
            canDelete={(document) => document.from_ambassador}
            pending={uploadDocuments.isPending || deleteDocument.isPending}
          />
        </Panel>
      )}
      {tab === "questions" && (
        <Panel
          title={t("details.conversationTitle")}
          subtitle={t("details.conversationHint")}
        >
          <DealConversation
            messages={deal.messages}
            viewer="ambassador"
            emptyText={t("details.noMessages")}
            onSend={
              canReply
                ? (message) =>
                    addMessage.mutateAsync({ dealId: deal.id, message })
                : undefined
            }
            pending={addMessage.isPending}
            sendLabel={
              questionsPending
                ? t("details.sendAnswer")
                : t("details.sendMessage")
            }
            placeholder={
              questionsPending
                ? t("details.answerPlaceholder")
                : t("details.messagePlaceholder")
            }
          />
        </Panel>
      )}

      <AmbassadorConfirmDialog
        open={withdrawOpen}
        title={t("details.withdrawTitle")}
        text={t("details.withdrawText")}
        inputLabel={t("details.withdrawReason")}
        confirmLabel={t("details.withdraw")}
        danger
        pending={withdraw.isPending}
        onClose={() => setWithdrawOpen(false)}
        onConfirm={(reason) =>
          withdraw.mutate(
            { dealId: deal.id, reason },
            { onSuccess: () => setWithdrawOpen(false) },
          )
        }
      />
    </>
  );
};

/** One of the ambassador's deals: data, released status history and questions. */
const DealDetails: React.FC = () => {
  const { dealId } = useParams();
  const location = useLocation();
  const deal = useMyDeal(Number(dealId));
  const submitted = Boolean(
    (location.state as { submitted?: boolean } | null)?.submitted,
  );

  if (deal.isLoading) return <LoadingState />;
  if (deal.isError || !deal.data)
    return <ErrorState onRetry={() => deal.refetch()} />;
  if (deal.data.registration_status === RegistrationStatus.DRAFT) {
    return <Navigate to={ambassadorPaths.editDraft(deal.data.id)} replace />;
  }
  return <DealDetailsContent deal={deal.data} submitted={submitted} />;
};

export default DealDetails;
